"use client";

import * as React from "react";
import { DayPicker } from "react-day-picker";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

export function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-row gap-2",
        month: "flex flex-col gap-4",
        month_caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium text-primary",
        nav: "flex items-center gap-1",
        button_previous: cn(
          "absolute left-1 h-7 w-7 flex items-center justify-center rounded-md border border-border bg-transparent text-muted",
          "hover:bg-surface-elevated hover:text-primary transition-colors"
        ),
        button_next: cn(
          "absolute right-1 h-7 w-7 flex items-center justify-center rounded-md border border-border bg-transparent text-muted",
          "hover:bg-surface-elevated hover:text-primary transition-colors"
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday: "text-muted rounded-md w-8 font-normal text-[0.8rem] text-center",
        week: "flex w-full mt-2",
        day: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 text-secondary",
          "[&:has([aria-selected])]:bg-brand-cyan/10 [&:has([aria-selected])]:text-brand-cyan",
          "[&:has([aria-selected][data-range-start])]:!bg-brand-cyan [&:has([aria-selected][data-range-start])]:!text-white",
          "[&:has([aria-selected][data-range-end])]:!bg-brand-cyan [&:has([aria-selected][data-range-end])]:!text-white"
        ),
        day_button: cn(
          "h-8 w-8 p-0 font-normal rounded-md text-xs transition-colors",
          "hover:bg-surface-elevated hover:text-primary",
          "focus:outline-none focus:ring-1 focus:ring-brand-cyan/50",
          "text-inherit",
          "data-[selected]:hover:bg-transparent data-[selected]:hover:text-inherit"
        ),
        selected: "bg-brand-cyan/10 text-brand-cyan rounded-md",
        today: "font-semibold text-brand-cyan",
        outside: "text-muted/40 opacity-50",
        disabled: "text-muted/30 opacity-30",
        range_start: "!bg-brand-cyan !text-white rounded-l-md hover:!bg-brand-cyan hover:!text-white [&>button]:hover:!bg-transparent [&>button]:hover:!text-white",
        range_end: "!bg-brand-cyan !text-white rounded-r-md hover:!bg-brand-cyan hover:!text-white [&>button]:hover:!bg-transparent [&>button]:hover:!text-white",
        range_middle: "bg-brand-cyan/10 text-brand-cyan rounded-none",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ),
      }}
      {...props}
    />
  );
}
